import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Hash, RotateCcw, Truck } from "lucide-react";
import { inViewProps, maskChild, Reveal, SectionLabel } from "./motion";
import { ProductCard } from "./ProductCard";
import { PodCard } from "./PodCard";
import { api } from "@/lib/api";

const PERKS = [
  { icon: Truck, t: "Spedizione gratuita sopra gli 80 €" },
  { icon: RotateCcw, t: "Reso facile entro 14 giorni" },
  { icon: Hash, t: "Pezzi numerati, tiratura limitata" },
];

const WORD = "sfushop".split("");

export const Store = () => {
  const { data, isLoading } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const products = Array.isArray(data) ? data : [];
  const official = products.filter((p) => p.source !== "gelato");
  const pod = products.filter((p) => p.source === "gelato");

  return (
    <section id="sfushop" data-testid="store-section" className="relative border-t border-line bg-[#070707] px-5 py-24 sm:px-8 lg:px-12 lg:py-32">
      <div className="flex items-center justify-between">
        <SectionLabel index="04">Lo store ufficiale</SectionLabel>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Drop 01 — Sfumature</span>
      </div>

      <motion.h2 aria-label="sfushop" data-testid="store-title" {...inViewProps()} className="mt-4 flex font-display text-[17vw] font-black lowercase leading-[0.9] tracking-[-0.05em]">
        {WORD.map((ch, i) => (
          <span key={`${ch}${i}`} className="block overflow-hidden">
            <motion.span className={`block ${i >= 3 ? "text-outline-cream" : "text-cream"}`} variants={maskChild(i * 0.05, 1.2)}>
              {ch}
            </motion.span>
          </span>
        ))}
      </motion.h2>

      <div className="mt-8 grid gap-8 border-t border-line pt-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="font-display text-2xl font-bold uppercase leading-tight tracking-tight sm:text-3xl">Vestiti che suonano come le mie canzoni.</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
          <p className="text-base leading-relaxed text-bone/70">
            Niente filtri, niente regole, niente produzioni infinite. Ogni capo sfushop lo disegno io e lo produco in piccole quantità, numerato uno per uno. È la mia musica, ma da indossare. Quando un drop finisce, finisce davvero.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid auto-rows-[540px] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
        {isLoading
          ? [0, 1, 2].map((i) => <div key={i} className={`animate-pulse bg-ink-2 ${i === 0 ? "md:col-span-2 lg:row-span-2" : ""}`} />)
          : official.map((p, i) => <ProductCard key={p.id} product={p} index={i} featured={i === 0} />)}
      </div>

      {pod.length > 0 && (
        <div className="mt-24" data-testid="pod-section">
          <div className="flex flex-wrap items-end justify-between gap-6 border-t border-line pt-8">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">(04.2) Collezione print on demand</p>
              <h3 className="mt-4 font-display text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl">Stampati quando<br />li scegli tu.</h3>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-bone/70">Ogni pezzo viene stampato su richiesta da Gelato nel laboratorio più vicino a te e spedito in 3–7 giorni lavorativi. Zero magazzino, zero sprechi.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-x-5 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {pod.map((p, i) => <PodCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}

      <div className="mt-10 grid gap-px border border-line bg-line sm:grid-cols-3">
        {PERKS.map(({ icon: Icon, t }) => (
          <div key={t} className="flex items-center gap-4 bg-[#070707] px-6 py-5">
            <Icon className="h-5 w-5 shrink-0 text-cream" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/80">{t}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
